import { useEffect, useRef } from 'react'

// ── Types ────────────────────────────────────────────────────────────
interface Ripple {
  x: number
  y: number
  startTime: number
  color: string // partial rgba string — e.g. 'rgba(34,211,238'
}

// ── Constants ────────────────────────────────────────────────────────
const DURATION   = 600        // ms — short enough to feel snappy, long enough to register
const MAX_RADIUS = 52         // px — subtle, never dominates
const COLORS     = [
  'rgba(34,211,238',   // cyan-400
  'rgba(139,92,246',   // violet-500
  'rgba(217,70,239',   // fuchsia-500
]
// Cap the live ripple pool so rapid clicking never piles up
const MAX_RIPPLES = 12

export function ClickEffect() {
  const canvasRef   = useRef<HTMLCanvasElement>(null)
  const ripples     = useRef<Ripple[]>([])
  const rafRef      = useRef<number | null>(null)
  const isLooping   = useRef(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // ── Resize canvas to cover the full viewport ──────────────────
    const resize = () => {
      canvas.width  = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // ── Click handler — O(1), just pushes to array ────────────────
    const handleClick = (e: MouseEvent) => {
      // Ignore clicks on buttons/inputs — let UI feel primary
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'A') return

      // Pick a themed color deterministically (cycles through palette)
      const color = COLORS[ripples.current.length % COLORS.length]

      ripples.current.push({
        x: e.clientX,
        y: e.clientY,
        startTime: performance.now(),
        color,
      })

      // Trim oldest if we exceed cap
      if (ripples.current.length > MAX_RIPPLES) {
        ripples.current.shift()
      }

      // Kick off the RAF loop if it isn't already running
      if (!isLooping.current) {
        isLooping.current = true
        rafRef.current = requestAnimationFrame(draw)
      }
    }
    window.addEventListener('click', handleClick)

    // ── Draw loop — only runs while ripples exist ─────────────────
    function draw(now: number) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      ripples.current = ripples.current.filter(r => {
        const t = Math.min((now - r.startTime) / DURATION, 1)

        // Cubic ease-out: starts fast, decelerates — feels physical
        const ease = 1 - Math.pow(1 - t, 3)

        // ── Outer ring ──────────────────────────────────────────────
        const outerR   = 6 + ease * (MAX_RADIUS - 6)
        const outerAlpha = 0.55 * (1 - t)

        ctx.beginPath()
        ctx.arc(r.x, r.y, outerR, 0, Math.PI * 2)
        ctx.strokeStyle = `${r.color},${outerAlpha.toFixed(3)})`
        ctx.lineWidth   = 1.5
        ctx.stroke()

        // ── Inner ring (30 % smaller, 25 % faster decay) ────────────
        const innerT    = Math.min(t * 1.35, 1)
        const innerEase = 1 - Math.pow(1 - innerT, 3)
        const innerR    = 3 + innerEase * (MAX_RADIUS * 0.45 - 3)
        const innerAlpha = 0.30 * (1 - innerT)

        if (innerAlpha > 0.005) {
          ctx.beginPath()
          ctx.arc(r.x, r.y, innerR, 0, Math.PI * 2)
          ctx.strokeStyle = `${r.color},${innerAlpha.toFixed(3)})`
          ctx.lineWidth   = 1
          ctx.stroke()
        }

        // ── Tiny centre dot (flashes and vanishes by t=0.25) ────────
        const dotAlpha = Math.max(0, 0.5 - t * 2)
        if (dotAlpha > 0.01) {
          ctx.beginPath()
          ctx.arc(r.x, r.y, 2.5, 0, Math.PI * 2)
          ctx.fillStyle = `${r.color},${dotAlpha.toFixed(3)})`
          ctx.fill()
        }

        return t < 1 // keep alive until fully done
      })

      if (ripples.current.length > 0) {
        rafRef.current = requestAnimationFrame(draw)
      } else {
        // No more ripples — pause the loop (zero CPU cost at rest)
        isLooping.current = false
        rafRef.current    = null
      }
    }

    // ── Cleanup ───────────────────────────────────────────────────
    return () => {
      window.removeEventListener('click', handleClick)
      window.removeEventListener('resize', resize)
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 9998 }}
      aria-hidden="true"
    />
  )
}
